import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AdminInvoiceLineItem, AdminInvoiceUserGroup } from '../../types/adminInvoice';
import { formatInvoiceMoney } from '../../utils/admin/formatInvoiceMoney';
import { formatSubscriptionDate, getSportLabel } from '../../utils/admin/formatSubscriptionDate';

interface InvoiceListTableProps {
  groups: AdminInvoiceUserGroup[];
}

function AmountSummaryBadge({ group }: { group: AdminInvoiceUserGroup }) {
  const summary = group.amount_summary;
  if (!summary) return null;

  const currency = summary.currency || 'usd';
  const paid = formatInvoiceMoney(summary.paid_amount, currency);
  const total = formatInvoiceMoney(summary.total_amount, currency);
  const pending = formatInvoiceMoney(summary.pending_amount, currency);
  const fullyPaid =
    Number.isFinite(summary.pending_amount)
      ? summary.pending_amount <= 0
      : summary.paid_amount >= summary.total_amount;

  // Always "Paid X of Y" — color signals balance; avoid "Due $0 of …".
  const className = fullyPaid
    ? 'bg-green-100 text-green-800'
    : 'bg-amber-100 text-amber-900';

  return (
    <span
      className={`mt-1.5 inline-flex px-2 py-0.5 rounded-full text-xs font-medium tabular-nums ${className}`}
      title={`Pending: ${pending}`}
    >
      Paid {paid} of {total}
    </span>
  );
}

function formatLineType(lineType: string): string {
  const t = (lineType || 'sport').toLowerCase();
  if (t === 'addon') return 'Addon';
  return 'Sport';
}

function formatSportCell(line: AdminInvoiceLineItem): string {
  if (line.line_type?.toLowerCase() === 'addon') {
    return line.addon_label?.trim() || '—';
  }
  if (line.sport_code?.trim()) {
    return getSportLabel(line.sport_code);
  }
  return '—';
}

const STATUS_CLASS: Record<string, string> = {
  paid: 'bg-green-100 text-green-800',
  due: 'bg-amber-100 text-amber-900',
};

const GRACE_EMAIL_LABELS = ['Activation', '7 days', '12 days', 'End mail'];

function formatEmailSentAt(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function DueEmailHover({ dates }: { dates: string[] }) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLSpanElement>(null);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const tooltipId = useId();
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sorted = [...dates].filter((d) => d?.trim()).sort((a, b) => a.localeCompare(b));

  const updatePosition = () => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const maxLeft = window.innerWidth - 300;
    setCoords({
      top: rect.bottom + 6,
      left: Math.max(8, Math.min(rect.left, maxLeft)),
    });
  };

  const clearCloseTimer = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  const handleEnter = () => {
    clearCloseTimer();
    setOpen(true);
    updatePosition();
  };

  const handleLeave = () => {
    clearCloseTimer();
    closeTimer.current = setTimeout(() => setOpen(false), 120);
  };

  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onScrollOrResize = () => updatePosition();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open]);

  useEffect(() => () => clearCloseTimer(), []);

  return (
    <>
      <span
        ref={triggerRef}
        className="inline-flex cursor-help"
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
        onFocus={handleEnter}
        onBlur={handleLeave}
        tabIndex={0}
        aria-describedby={open ? tooltipId : undefined}
      >
        <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium capitalize bg-amber-100 text-amber-900 underline decoration-dotted decoration-amber-500 underline-offset-2">
          Due
        </span>
      </span>
      {open &&
        coords &&
        createPortal(
          <div
            id={tooltipId}
            role="tooltip"
            className="fixed z-[100] min-w-[240px] max-w-sm rounded-md border border-gray-200 bg-white shadow-lg"
            style={{ top: coords.top, left: coords.left }}
            onMouseEnter={clearCloseTimer}
            onMouseLeave={handleLeave}
          >
            <div className="px-3 py-2 border-b border-gray-100 text-xs font-semibold text-gray-700">
              Grace emails sent
            </div>
            {sorted.length === 0 ? (
              <div className="px-3 py-2 text-xs text-gray-500">No grace emails sent yet</div>
            ) : (
              <ul className="px-3 py-2 space-y-1.5">
                {sorted.map((iso, index) => (
                  <li key={iso} className="flex items-baseline justify-between gap-4 text-xs">
                    <span className="font-medium text-gray-600 shrink-0">
                      {GRACE_EMAIL_LABELS[index] ?? `Email ${index + 1}`}
                    </span>
                    <span className="text-gray-800 tabular-nums whitespace-nowrap">
                      {formatEmailSentAt(iso)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>,
          document.body
        )}
    </>
  );
}

function StatusBadge({
  status,
  emailSentDates,
}: {
  status: string;
  emailSentDates?: string[] | null;
}) {
  const normalized = (status || '').toLowerCase();
  if (normalized === 'due') {
    return <DueEmailHover dates={emailSentDates ?? []} />;
  }
  const label = normalized === 'paid' ? 'Paid' : status || '—';
  const className = STATUS_CLASS[normalized] ?? 'bg-gray-100 text-gray-700';
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium capitalize ${className}`}>
      {label}
    </span>
  );
}

function formatDateTime(value: string | null): string {
  if (!value?.trim()) return '—';
  return formatSubscriptionDate(value);
}

const InvoiceListTable: React.FC<InvoiceListTableProps> = ({ groups }) => {
  if (groups.length === 0) {
    return (
      <div className="text-center p-10 bg-gray-50 rounded-lg text-gray-500">
        No invoices found
      </div>
    );
  }

  return (
    <div className="overflow-x-auto border border-gray-200 rounded-lg">
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Team</th>
            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sport</th>
            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Generated on</th>
            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Due date</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {groups.map((group) => {
            const lineCount = Math.max(group.lines.length, 1);
            const lines = group.lines.length > 0 ? group.lines : [null];

            return lines.map((line, lineIndex) => (
              <tr
                key={`${group.billing_user_id}-${line?.payment_id ?? 'empty'}-${line?.sport_code ?? lineIndex}`}
                className="hover:bg-gray-50 align-top"
              >
                {lineIndex === 0 && (
                  <>
                    <td className="px-3 py-3 border-r border-gray-100" rowSpan={lineCount}>
                      {group.full_name?.trim() ? (
                        <div className="font-medium text-gray-900">{group.full_name}</div>
                      ) : null}
                      <div className={`text-sm ${group.full_name?.trim() ? 'text-gray-600' : 'font-medium text-gray-900'}`}>
                        {group.username}
                      </div>
                      <div className="text-xs text-gray-500">{group.email?.trim() ? group.email : '—'}</div>
                      <AmountSummaryBadge group={group} />
                    </td>
                    <td className="px-3 py-3 border-r border-gray-100" rowSpan={lineCount}>
                      {group.team_name?.trim() ? group.team_name : '—'}
                    </td>
                  </>
                )}
                {line ? (
                  <>
                    <td className="px-3 py-3 text-gray-700">{formatLineType(line.line_type)}</td>
                    <td className="px-3 py-3 text-gray-800 font-medium">{formatSportCell(line)}</td>
                    <td className="px-3 py-3 text-gray-900 tabular-nums whitespace-nowrap">
                      {formatInvoiceMoney(line.amount, line.currency)}
                    </td>
                    <td className="px-3 py-3">
                      <StatusBadge status={line.status} emailSentDates={line.email_sent_dates} />
                    </td>
                    <td className="px-3 py-3 text-gray-600 whitespace-nowrap">
                      {formatDateTime(line.generated_at)}
                    </td>
                    <td className="px-3 py-3 text-gray-600 whitespace-nowrap">
                      {formatDateTime(line.due_date)}
                    </td>
                  </>
                ) : (
                  <td colSpan={6} className="px-3 py-3 text-gray-400 text-sm">
                    No line items
                  </td>
                )}
              </tr>
            ));
          })}
        </tbody>
      </table>
    </div>
  );
};

export default InvoiceListTable;
